import {type WBSlateElementType} from '../../components/Slate/WBSlate/types';
import type * as FilterTypes from '../filterTypes';
import {
  type FilterV2Root,
  isFilterV2,
  migrateLegacyFiltersToV2,
} from '../filterV2';
import type * as RunTypes from '../runTypes';
import {type SlateReport} from './types';

export interface MigrateReportRunSetFiltersResult {
  spec: SlateReport;
  isFiltersMigrated: boolean;
}

/**
 * Migrate run set filters in a Slate report from legacy format to FilterV2.
 *
 * Run sets containing legacy (v1) filters are converted to the flat FilterV2
 * format. V2 filters are passed through unchanged.
 *
 * Mirrors the traversal pattern in migrateReportRunSetProjects: top-level
 * blocks and recursively nested collapsedChildren are both handled.
 */
export function migrateReportRunSetFilters(
  spec: SlateReport
): MigrateReportRunSetFiltersResult {
  let shouldUpdateSpec = false;

  function migrateBlock(block: WBSlateElementType): WBSlateElementType {
    let updatedBlock = block;

    if (block.collapsedChildren) {
      const updatedChildren = block.collapsedChildren.map(migrateBlock);
      updatedBlock = {...updatedBlock, collapsedChildren: updatedChildren};
    }

    if (updatedBlock.type !== 'panel-grid' || !updatedBlock.metadata.runSets) {
      return updatedBlock;
    }

    const runSets = updatedBlock.metadata.runSets;
    const updatedRunSets = runSets.map(rs => {
      if (rs.filters == null) {
        return rs;
      }
      const migrated = migrateFilters(rs.filters);
      if (migrated === rs.filters) {
        return rs;
      }
      shouldUpdateSpec = true;
      return {...rs, filters: migrated};
    });

    if (!shouldUpdateSpec) {
      return updatedBlock;
    }

    return {
      ...updatedBlock,
      metadata: {
        ...updatedBlock.metadata,
        runSets: updatedRunSets,
      },
    };
  }

  const updatedBlocks = spec.blocks.map(migrateBlock);

  if (!shouldUpdateSpec) {
    return {spec, isFiltersMigrated: false};
  }

  return {
    spec: {
      ...spec,
      blocks: updatedBlocks,
    },
    isFiltersMigrated: true,
  };
}

function migrateFilters(
  filters: FilterV2Root | FilterTypes.RootFilter<RunTypes.Key>
): FilterV2Root {
  if (isFilterV2(filters)) {
    return filters;
  }

  return migrateLegacyFiltersToV2(filters);
}
